import React, { useState, useEffect, useRef } from 'react'
import { MicrophoneIcon } from '@shopify/polaris-icons'

interface VoiceInputProps {
    onCommand: (command: string) => void
    autoFocus?: boolean
}

declare global {
    interface Window {
        SpeechRecognition?: typeof SpeechRecognition
        webkitSpeechRecognition?: typeof SpeechRecognition
    }
}

const VoiceInput = ({ onCommand, autoFocus }: VoiceInputProps) => {
    const [isListening, setIsListening] = useState(false)
    const [transcript, setTranscript] = useState('')
    const recognitionRef = useRef<SpeechRecognition | null>(null)
    const buttonRef = useRef<HTMLButtonElement>(null)

    useEffect(() => {
        if (autoFocus && buttonRef.current) {
            buttonRef.current.focus()
        }
    }, [autoFocus])

    useEffect(() => {
        const handleKeyPress = (e: KeyboardEvent) => {
            if (e.code === 'Space' && document.activeElement === buttonRef.current) {
                e.preventDefault()
                toggleListening()
            }
        }

        window.addEventListener('keydown', handleKeyPress)
        return () => window.removeEventListener('keydown', handleKeyPress)
    }, [isListening])

    useEffect(() => {
        if ('SpeechRecognition' in window || 'webkitSpeechRecognition' in window) {
            const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition
            recognitionRef.current = new SpeechRecognition()
            recognitionRef.current.continuous = true
            recognitionRef.current.interimResults = true

            recognitionRef.current.onresult = (event) => {
                let finalTranscript = ''
                for (let i = event.resultIndex; i < event.results.length; i++) {
                    const transcript = event.results[i][0].transcript
                    if (event.results[i].isFinal) {
                        finalTranscript += transcript
                    }
                }
                if (finalTranscript) {
                    setTranscript(finalTranscript)
                }
            }

            recognitionRef.current.onend = () => {
                setIsListening(false)
            }

            recognitionRef.current.onerror = (event) => {
                console.error('Speech recognition error:', event.error)
                setIsListening(false)
            }
        }

        return () => {
            if (recognitionRef.current) {
                recognitionRef.current.stop()
            }
        }
    }, [])

    const toggleListening = () => {
        if (!recognitionRef.current) return

        if (isListening) {
            recognitionRef.current.stop()
            if (transcript) {
                onCommand(transcript)
                setTranscript('')
            }
        } else {
            try {
                recognitionRef.current.start()
                setIsListening(true)
                setTranscript('')
            } catch (error) {
                console.error('Error starting speech recognition:', error)
            }
        }
    }

    return (
        <div className="has-text-centered">
            <button
                ref={buttonRef}
                onClick={toggleListening}
                className={`button is-medium is-rounded ${isListening ? 'is-info' : 'is-light'}`}
            >
                <span className="icon is-medium">
                    <MicrophoneIcon type='filled' />
                </span>
            </button>
            <p className="mt-3 has-text-grey">
                {isListening ? 'Listening...' : 'Click or press Space and start speaking'}
            </p>
            {transcript && <p className="mt-2 is-size-7 has-text-grey-light">{transcript}</p>}
        </div>
    )
}

export default React.memo(VoiceInput)
